import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from 'recharts';
import { Box, Typography, Stack, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const MONTH_NAMES = { 1: 'Janvier', 2: 'Février', 3: 'Mars', 4: 'Avril', 5: 'Mai', 6: 'Juin', 7: 'Juillet', 8: 'Août', 9: 'Septembre', 10: 'Octobre', 11: 'Novembre', 12: 'Décembre' };

/**
 * Tooltip Personnalisé Premium (Style Glassmorphism)
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
        border: '1px solid #e2e8f0',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.08)',
      }}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label} 2026
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#2563eb' }} />
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>
            {payload[0].value} {payload[0].value > 1 ? 'commandes' : 'commande'}
          </Typography>
        </Stack>
      </Box>
    );
  }
  return null;
};

/**
 * Composant pour afficher les volumes sous forme de badges professionnels discrets
 */
const ProLabel = (props) => {
  const { x, y, value } = props;
  if (value === 0) return null; // Masque les mois sans activité pour garder le tracé net

  return (
    <g>
      {/* Petit conteneur d'arrière-plan pour isoler le texte des lignes de grille */}
      <rect 
        x={x - 14} 
        y={y - 25} 
        width={28} 
        height={18} 
        rx={4} 
        fill="#f8fafc" // Slate 50
        stroke="#e2e8f0" // Slate 200
        strokeWidth={1}
      />
      <text 
        x={x} 
        y={y - 13} 
        fill="#0f172a" // Slate 900
        fontSize={11} 
        fontWeight={700} 
        textAnchor="middle"
      >
        {value}
      </text>
    </g>
  );
};

export default function OrdersChart({ data }) {

  // 1. Reconstruction stricte de la chronologie sur les 6 derniers mois (Jusqu'à Mai 2026)
  const formattedData = useMemo(() => {
    const currentMonth = 5; // Mai 2026
    const currentYear = 2026;
    const result = [];

    for (let i = 5; i >= 0; i--) {
      let targetMonth = currentMonth - i;
      let targetYear = currentYear;

      if (targetMonth <= 0) {
        targetMonth += 12;
        targetYear -= 1;
      }

      const match = data?.find(d => d.month === targetMonth);

      result.push({
        month: targetMonth,
        year: targetYear,
        name: MONTH_NAMES[targetMonth],
        orders: match ? Number(match.orders) : 0
      });
    }
    return result;
  }, [data]);

  // 2. Calculs statistiques avancés (KPIs et Tendances métier)
  const analytics = useMemo(() => {
    if (!formattedData.length) return { total: 0, peak: 0, average: 0, growthRate: 0 };
    
    const orderValues = formattedData.map(d => d.orders);
    const total = orderValues.reduce((a, b) => a + b, 0);
    const peak = Math.max(...orderValues);
    const average = Math.round(total / formattedData.length);

    // Comparaison MoM : Mai (index 5) avec Avril (index 4)
    const currentMonthOrders = orderValues[5] || 0;
    const previousMonthOrders = orderValues[4] || 0;
    
    let growthRate = 0;
    if (previousMonthOrders > 0) {
      growthRate = Math.round(((currentMonthOrders - previousMonthOrders) / previousMonthOrders) * 100);
    } else if (previousMonthOrders === 0 && currentMonthOrders > 0) {
      growthRate = 100;
    }

    return { total, peak, average, growthRate };
  }, [formattedData]);

  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={320} sx={{ border: '1px dashed #e2e8f0', borderRadius: '12px' }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
          Aucune donnée analytique disponible pour cette période
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
      
      {/* SECTION EN-TÊTE : Indicateurs de Performance Clés (KPIs) */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ pb: 1, borderBottom: '1px solid #f1f5f9' }}>
        
        <Stack direction="row" spacing={4}>
          <Box>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.075em', mb: 0.5 }}>
              Volume Global (6m)
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.04em' }}>
              {analytics.total.toLocaleString('fr-FR')}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.075em', mb: 0.5 }}>
              Moyenne Mensuelle
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#334155', letterSpacing: '-0.04em' }}>
              {analytics.average}
            </Typography>
          </Box>
        </Stack>

        {/* Badge de Tendance Dynamique (MoM) */}
        <Box sx={{ pt: { xs: 1, sm: 0 } }}>
          <Chip
            icon={analytics.growthRate >= 0 ? <TrendingUpIcon style={{ color: '#16a34a' }} /> : <TrendingDownIcon style={{ color: '#dc2626' }} />}
            label={`${analytics.growthRate >= 0 ? '+' : ''}${analytics.growthRate}% vs mois dernier`}
            variant="outlined"
            sx={{
              fontWeight: 700,
              fontSize: '13px',
              color: analytics.growthRate >= 0 ? '#15803d' : '#b91c1c',
              backgroundColor: analytics.growthRate >= 0 ? '#f0fdf4' : '#fef2f2',
              borderColor: analytics.growthRate >= 0 ? '#bbf7d0' : '#fecaca',
              px: 0.5,
              height: '32px',
              '& .MuiChip-icon': { fontSize: '18px' }
            }}
          />
        </Box>
      </Stack>

      {/* SECTION GRAPHIQUE AVEC LABELS INTEGRES */}
      <Box sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={formattedData} margin={{ top: 25, right: 15, left: -20, bottom: 0 }}>
            
            <defs>
              <linearGradient id="proBlueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2563eb" stopOpacity={0.08}/>
                <stop offset="95%" stopColor="#2563eb" stopOpacity={0.00}/>
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#f1f5f9" />
            
            <XAxis 
              dataKey="name" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }}
              dy={10}
            />
            
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 500 }}
              allowDecimals={false}
            />
            
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }} />

            {/* Ligne de repère de la moyenne */}
            <ReferenceLine 
              y={analytics.average} 
              stroke="#cbd5e1" 
              strokeDasharray="5 5" 
              strokeWidth={1.2}
              label={{ 
                value: `Moy: ${analytics.average}`, 
                position: 'insideBottomRight', 
                fill: '#94a3b8', 
                fontSize: 11, 
                fontWeight: 600,
                offset: 8
              }} 
            />
            
            {/* Ligne & Aire Principale */}
            <Area 
              type="monotone" 
              dataKey="orders" 
              stroke="#2563eb" 
              strokeWidth={2.5} 
              fillOpacity={1} 
              fill="url(#proBlueGradient)"
              dot={{ r: 4, fill: '#ffffff', stroke: '#2563eb', strokeWidth: 2.5 }}
              activeDot={{ 
                r: 6, 
                fill: '#2563eb', 
                stroke: '#ffffff', 
                strokeWidth: 2,
                style: { filter: 'drop-shadow(0px 4px 12px rgba(37,99,235,0.35))' }
              }} 
            >
              {/* INDICATION CHRONOLOGIQUE : Ajoute les nombres au-dessus de chaque mois */}
              <LabelList dataKey="orders" content={<ProLabel />} />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

OrdersChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.number.isRequired,
      orders: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    })
  ).isRequired,
};